// src/utils/seo/publicPageMeta.js
//
// Title and description for every PUBLIC page, in one place. The signed-in
// pages set their own heads (and are blocked in robots.txt); these are the
// pages search engines see, so each gets a distinct title under ~60
// characters and a description under ~155 that says what the page is for in
// the words people search with.
//
// Keep the route list in step with scripts/prerender.js and robots.txt.

const SITE_NAME = "CareerClaw";

export const PUBLIC_PAGE_META = {
  "/": {
    title: "ATS-Friendly Resume Builder & Job Search Tools | CareerClaw",
    description:
      "Build an ATS-friendly resume, tailor it to any job description, write cover letters, prep for interviews and track every application in one place. Free to start.",
  },
  "/home": {
    title: "Resume, Cover Letter & Interview Prep in One Place | CareerClaw",
    description:
      "One workspace for the whole job search: an ATS-friendly resume, tailored versions per job, AI cover letters, interview practice and an application tracker.",
  },
  "/select-template": {
    title: "ATS-Friendly Resume Templates for Every Career Stage | CareerClaw",
    description:
      "Clean, single-column resume templates that applicant tracking systems read correctly. Pick a layout, build your resume, download as PDF or Word.",
  },
  "/services": {
    title: "Professional Resume Writing Services | CareerClaw",
    description:
      "Work with a professional resume writer. ATS-optimised resumes, LinkedIn profiles and cover letters written for the roles you actually want.",
  },
  "/needresume": {
    title: "Get Expert Help With Your Resume | CareerClaw",
    description:
      "Not sure where to start? Get expert help building or rewriting your resume, with a clear plan for the roles you are targeting.",
  },
  "/testimonials": {
    title: "What Job Seekers Say About CareerClaw | Testimonials",
    description:
      "Stories from job seekers who built ATS-friendly resumes, landed interviews and kept their search organised with CareerClaw.",
  },
  "/courses": {
    title: "Career Courses & Job Search Guides | CareerClaw",
    description:
      "Short courses on resumes, interviews and job search strategy to help you land your next role faster.",
  },
  "/register": {
    title: "Create Your Free CareerClaw Account",
    description:
      "Sign up free to build an ATS-friendly resume, tailor it to jobs, write cover letters and track your applications.",
  },
  "/tools/ats-resume-checker": {
    title: "Free ATS Resume Checker - See What the Parser Reads | CareerClaw",
    description:
      "Paste your resume and get a plain checklist of what an applicant tracking system will read and miss - contact details, headings, dates, bullets - with the fix for each. Free, no sign-up.",
  },
  "/tools/resume-keyword-match": {
    title: "Free Resume Keyword Scanner: Match Your Resume to a Job Description",
    description:
      "Paste a job description and your resume to see which keywords you already have and which are missing. Runs in your browser, no account needed.",
  },
  "/tools/cover-letter-generator": {
    title: "Free Cover Letter Generator - A Clean Draft in One Minute | CareerClaw",
    description:
      "Enter the role, the company and two results you are proud of. Get a professional four-paragraph cover letter to copy and edit. Free, nothing invented.",
  },
  "/tools/linkedin-to-resume": {
    title: "Turn Your LinkedIn Profile Into an ATS-Friendly Resume | CareerClaw",
    description:
      "Export your LinkedIn profile as a PDF and convert it into a single-column resume that applicant tracking systems read correctly, ready to tailor to any job.",
  },
  "/tools/job-application-tracker": {
    title: "Free Job Application Tracker Template (Excel) | CareerClaw",
    description:
      "Download a ready-made Excel job application tracker with status, dates, follow-up and the resume you sent - or use the online tracker that does the work for each job.",
  },
  "/login": {
    title: "Sign In | CareerClaw",
    description:
      "Sign in to your CareerClaw account to continue building your resume and tracking your job search.",
  },
};

/** Meta for a path, or null for pages that manage their own head. */
export const getPublicPageMeta = (pathname) => {
  const clean = String(pathname || "/").replace(/\/+$/, "") || "/";
  return PUBLIC_PAGE_META[clean] || null;
};

/**
 * schema.org data for the home page: who publishes the site and that it is a
 * web application with a free tier. Rendered only on "/" so the same
 * organisation is not declared on every page.
 */
export const buildHomeStructuredData = (origin) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${origin}/#organization`,
      name: SITE_NAME,
      url: `${origin}/`,
      logo: `${origin}/logo192.png`,
    },
    {
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      url: `${origin}/`,
      name: SITE_NAME,
      publisher: { "@id": `${origin}/#organization` },
    },
    {
      "@type": "SoftwareApplication",
      name: SITE_NAME,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `${origin}/`,
      description: PUBLIC_PAGE_META["/"].description,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free to start; downloads and metered AI on a paid plan.",
      },
      featureList: [
        "ATS-friendly resume builder",
        "Tailor a resume to a job description",
        "AI cover letter generator",
        "Interview question practice",
        "Job application tracker",
      ],
    },
  ],
});

export default { PUBLIC_PAGE_META, getPublicPageMeta, buildHomeStructuredData };
